import { groupBy } from "lodash-es";
import { useHistory, useLocation } from "react-router-dom";
import { BulkClassQuery } from "src/api/Class";
import { BulkOrganizationQuery } from "src/api/Organization";
import { TagsQuery } from "src/api/Tag";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import { AddItemHeader } from "src/components/forms/AddItemHeader";
import Container from "src/components/layout/Container";
import { BulkEditCheckoutModal } from "src/components/modals/BulkEditCheckoutModal";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { modals } from "src/utils/modals";
import tw from "src/utils/tw";
import { useSearchParams } from "src/utils/useSearchParams";
import {
  bulkClassVariables,
  bulkOrganizationVariables,
  tags,
  tagsVariables,
} from "types/code-generator";

interface Props {
  organization?: boolean;
  needs?: boolean;
}

export default function BulkEditTags(props: Props) {
  const history = useHistory();
  const location = useLocation();
  const store = useStore();
  const params = useSearchParams();

  const ids = params.getAll("id");
  const excluded = params.has("exclude");

  const tagsVariables: tagsVariables = {
    where: {
      departmentId: {
        in: store.organization.departments.map((d) => d.id),
      },
    },
  };

  if (props.organization) {
    if (props.needs) {
      tagsVariables.where.need = true;
    } else {
      tagsVariables.where.organization = true;
    }

    delete tagsVariables.where.departmentId;
  }

  const queryTags = useApi(TagsQuery, {
    variables: tagsVariables,
  });

  const variables: bulkClassVariables = {
    filter: location.search,
    where: {
      organizationId: store.organization.id,
      id:
        ids.length > 0
          ? excluded
            ? { notIn: ids }
            : {
                in: ids,
              }
          : undefined,
    },
  };

  const organizationVariables: bulkOrganizationVariables = {
    filter: location.search,
  };

  const bulkEditQuery = useApi(BulkClassQuery, {
    skip: props.organization,
    variables,
  });

  const bulkOrganizationEditQuery = useApi(BulkOrganizationQuery, {
    skip: !props.organization,
    variables: organizationVariables,
  });

  const counts = bulkEditQuery?.bulkClass;
  const organizationCounts = bulkOrganizationEditQuery?.bulkOrganization;
  const tags = queryTags.tags || [];

  function onCancel() {
    history.goBack();
  }

  function onNetworkAction() {
    if (!props.organization) {
      bulkEditQuery.refetch();
    }

    if (props.organization) {
      bulkOrganizationEditQuery.refetch();
    }
  }

  function bulkEditTags(tag: tags["tags"][0]) {
    modals.show(BulkEditCheckoutModal, {
      field: props.needs ? "needs" : "tags",
      counts: props.organization ? organizationCounts : counts,
      itemId: tag.id,
      itemTitle: tag.title,
      onNetworkAction,
      organization: props.organization,
    });
  }

  let type = "offering";
  let title = "Tags";

  if (props.organization) {
    type = "organization";

    if (props.needs) {
      title = "Needs";
    }
  }

  const grouped = groupBy(tags, "department.id");

  console.log("counts", counts);

  // console.log("offering", offering);
  return (
    <>
      <AddItemHeader>
        <Container large>
          <BackButton backText="Return to Bulk Edit" />
          <h2 className="mb-0 flex-1">{title}</h2>
        </Container>
      </AddItemHeader>

      <Container
        large
        className={tw("bg-white pb-24", props.organization ? "pt-8" : "pt-24")}
      >
        <Container fullSmall>
          <p className="mb-4 pt-4">
            Select a {props.needs ? "need" : "tag"} to enable or disable for all
            selected {type}s.
          </p>

          <div className="">
            {Object.keys(grouped).map((departmentId) => {
              let total = counts?.total || 0;

              if (store.organization.hasDepartments) {
                total = counts?.totalPerDepartment[departmentId] || 0;
              }

              let header = (
                <>
                  <hr className="my-0" />
                </>
              );

              const tags = grouped[departmentId];

              if (store.organization.hasDepartments) {
                header = (
                  <div className="pt-4">
                    <div className="step-subhead">
                      {grouped[departmentId][0].department.title}
                    </div>
                    <hr className="mt-0 border-darkGray" />
                    <p className="px-4">
                      {total.toLocaleString()} from your selected list may be
                      edited to enable/disable these settings.
                    </p>
                    <hr className="my-0" />
                  </div>
                );
              }

              return (
                <div key={departmentId}>
                  {header}
                  <div className="divide-y divide-borderGray">
                    {tags.map((c) => {
                      let count =
                        counts?.tags[departmentId]?.[c.id]?.length || 0;

                      let enabled = `Enabled on ${count.toLocaleString()} of ${total.toLocaleString()} selected ${type}${
                        total !== 1 ? "s" : ""
                      }.`;

                      if (count === total) {
                        enabled = `Enabled on all ${total.toLocaleString()} selected ${type}${
                          total !== 1 ? "s" : ""
                        }.`;
                      }

                      if (props.organization) {
                        count =
                          (organizationCounts?.tags &&
                            organizationCounts.tags[c.id]?.length) ||
                          0;

                        if (props.needs) {
                          count =
                            (organizationCounts?.needs &&
                              organizationCounts.needs[c.id]?.length) ||
                            0;
                        }

                        enabled = `Enabled on ${count.toLocaleString()} of ${organizationCounts.total?.toLocaleString()} selected ${type}${
                          count !== 1 ? "s" : ""
                        }.`;

                        if (count === organizationCounts.total) {
                          enabled = `Enabled on all ${organizationCounts.total.toLocaleString()} selected ${type}${
                            organizationCounts.total !== 1 ? "s" : ""
                          }.`;
                        }
                      }

                      return (
                        <button
                          key={c.id}
                          className="flex w-full items-center px-4 py-4 text-left"
                          type="button"
                          onClick={() => bulkEditTags(c)}
                        >
                          <div className="flex-1">
                            <strong className="text-dark">{c.title}</strong>{" "}
                            <div className="text-sm text-empty">{enabled}</div>
                          </div>
                          <i className="far fa-chevron-right text-organization" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <hr className="my-0" />
          </div>

          <div className="pb-12 pt-8">
            <BorderButton onClick={onCancel}>Return to Bulk Edit</BorderButton>
          </div>
        </Container>
      </Container>
    </>
  );
}
